import {
  Button,
  FormGroupHeader,
  FormGroupHeaderActions,
  FormGroupHeaderTitle,
  Icon,
  Stack,
  cn,
} from '@eluve/components';

import { SettingsGroup } from './SettingsGroup';

/**
 * Outline settings card for a managed list (snippets, conversation
 * starters, output preferences): a count header with actions (e.g. a
 * "Create New" button), followed by the list rows.
 */
export const SettingsListGroup = ({
  title,
  description,
  countLabel,
  actions,
  children,
}: {
  title?: string;
  description?: React.ReactNode;
  /** Header summary of the list, e.g. "4 snippets". */
  countLabel: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <SettingsGroup title={title} description={description} variant="outline">
    <FormGroupHeader className="px-6 py-4">
      <FormGroupHeaderTitle>{countLabel}</FormGroupHeaderTitle>
      {actions != null && (
        <FormGroupHeaderActions className="gap-2">
          {actions}
        </FormGroupHeaderActions>
      )}
    </FormGroupHeader>
    {children}
  </SettingsGroup>
);

/**
 * Row of a {@link SettingsListGroup}: item content on the left, edit and
 * delete buttons revealed on hover or focus on the right.
 */
export const SettingsListRow = ({
  children,
  leading,
  editLabel,
  deleteLabel,
  onEdit,
  onDelete,
  disabled = false,
}: {
  children: React.ReactNode;
  /** Slot before the item content, e.g. a drag handle. */
  leading?: React.ReactNode;
  editLabel: string;
  deleteLabel: string;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) => (
  <Stack
    direction="horizontal"
    justify="between"
    align="center"
    gap={2}
    className={cn(
      'group px-6 py-7 transition-colors hover:bg-bg-secondary',
      disabled && 'pointer-events-none opacity-50',
    )}
  >
    {leading}
    <Stack gap={1} align="start" className="min-w-0 flex-1">
      {children}
    </Stack>
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
      fullWidth={false}
    >
      <Button
        variant="outline"
        size="sm"
        onClick={onDelete}
        aria-label={deleteLabel}
      >
        <Icon name="Trash2" size="auto" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onEdit}
        aria-label={editLabel}
      >
        <Icon name="Pencil" size="auto" />
      </Button>
    </Stack>
  </Stack>
);
